import { CircularProgress, Divider, Stack, Typography } from '@mui/material';
import { type RefObject, useMemo } from 'react';
import { Conversation, useConversationStatusOptions } from 'src/graphql';
import { useArrowNavigation } from 'src/utils/hooks/use-arrow-navigation';
import { SuggestionGroup } from './suggestion-group';
import { MergeSuggestionsStatus } from './suggestions.types';
import { useMergeTargetSuggestions } from './use-merge-target-suggestions';

interface MergeTargetSuggestionsProps {
  origin: Conversation;
  onSelect: (conversation: Conversation) => void;
  /** ArrowUp on the first suggestion, or typing, hands focus back, e.g. to the search above. */
  onExitTop?: () => void;
  listRef: RefObject<HTMLDivElement | null>;
}

export const MergeTargetSuggestions = ({
  origin,
  onSelect,
  onExitTop,
  listRef,
}: MergeTargetSuggestionsProps) => {
  const handleArrowKeys = useArrowNavigation({ wrap: false, onExitTop });
  const statusOptions = useConversationStatusOptions();

  const { status, groups } = useMergeTargetSuggestions({
    origin,
    enabled: Boolean(origin),
  });

  const statusLabelById = useMemo(() => {
    const map: Record<string, string> = {};
    statusOptions.forEach((option) => {
      map[option.value] = option.label;
    });
    return map;
  }, [statusOptions]);

  const getStatusLabel = (statusId: string): string => statusLabelById[statusId] ?? statusId;

  return (
    <Stack
      ref={listRef}
      gap={1.5}
      onKeyDown={(event) => handleArrowKeys(event, listRef)}
      data-testid="merge-conversation-suggestions"
    >
      <Divider>or</Divider>
      <Typography variant="subtitle2">Suggested merge targets</Typography>

      {status === MergeSuggestionsStatus.Loading ? (
        <Stack direction="row" gap={1} alignItems="center" py={1}>
          <CircularProgress size="1rem" />
          <Typography variant="body2" color="text.secondary">
            Finding suggested conversations…
          </Typography>
        </Stack>
      ) : null}

      {status === MergeSuggestionsStatus.Empty ? (
        <Typography variant="body2" color="text.secondary">
          No suggestions right now. Use search to pick a target, or open related conversations
          in another tab.
        </Typography>
      ) : null}

      {status === MergeSuggestionsStatus.Error ? (
        <Typography variant="body2" color="error">
          Couldn&apos;t load suggestions. Use search to pick a target.
        </Typography>
      ) : null}

      {status === MergeSuggestionsStatus.Ready ? (
        <>
          <Typography variant="caption" color="text.secondary">
            Tap a suggestion to select it as the merge target. Manual search stays available.
          </Typography>
          {groups.map((group) => (
            <SuggestionGroup
              key={group.bucket}
              group={group}
              getStatusLabel={getStatusLabel}
              onSelect={onSelect}
            />
          ))}
        </>
      ) : null}
    </Stack>
  );
};
